import React, { useState } from "react";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import AuthForm from "../../components/AuthForm/AuthForm";
import { useLoginMutation } from "../../store/api/AuthApi";
import InputField from "./../../components/InputFields/InputField";
import { useAuth } from "../../contexts/AuthContext";

const Login = () => {
  const location = useLocation();
  // Register redirects here with the just-created email pre-filled.
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const [loginMutation, { isLoading }] = useLoginMutation();
  const { login } = useAuth();

  const validateForm = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid";
    }
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await loginMutation({ email, password }).unwrap();

      if (response.success) {
        const { token, user, message } = response.data;
        login(token, user);

        toast.success(message || "Login successful.");
        navigate("/reporting", { replace: true });
      } else {
        toast.error(response.message || "Login failed");
      }
    } catch (error) {
      // Wrong email/password shows here as an inline field error too, not
      // just a toast, so the failure is obvious without losing what was typed.
      const message =
        error.data?.message ||
        error.data?.error?.message ||
        "Invalid email or password. Please try again.";
      setErrors({ password: message });
      toast.error(message);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <AuthForm
      title="Welcome back"
      subtitle="Sign in to access your QC dashboard."
      onSubmit={handleLogin}
      buttonText={isLoading ? "Signing in..." : "Sign In"}
      isButtonDisabled={isLoading}
      fields={
        <>
          <InputField
            type="email"
            placeholder="you@example.com"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            icon={<FaEnvelope />}
            error={errors.email}
            disabled={isLoading}
            autoComplete="email"
          />
          <InputField
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            icon={<FaLock />}
            error={errors.password}
            disabled={isLoading}
            autoComplete="current-password"
            additionalIcon={
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="btn btn-sm p-0 border-0 bg-transparent"
                style={{ color: "var(--text-secondary)" }}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            }
          />
          <div className="text-center mt-4">
            <p className="mb-0" style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
              Don't have an account?{" "}
              <Link to="/register" style={{ color: "var(--accent)", fontWeight: 600 }}>
                Sign up here
              </Link>
            </p>
          </div>
        </>
      }
    />
  );
};

export default React.memo(Login);
